import { useTranslation } from 'next-i18next';
import { useServices } from '@/context/ServicesProvider';
import React, { useEffect } from 'react';
import { BusquedaResultadoTelemetry, TelemetryRowObject, TipoBusquedaTelemetry, TipoPeriodoTelemetry, TipoSeccionLibrosEstadisticas } from '@/objects/Telemetry';
import TituloSeccionH2 from '@/components/elements/TituloSeccionH2';
import CardDatoHorizontal from '@/components/elements/CardDatoHorizontal';
import TopNavigation from '@/components/elements/TopNavigation';
import { OpcionTopNavigation } from '@/objects/Menus';
import { obtenerImagenes } from '@/util/Articulos';
import { useNotificationContext } from '@/context/NotificationContext';
import RowNoData from '@/components/elements/RowNoData';

interface LibrosPopularesProps {
    tipoSeccionLibros: TipoSeccionLibrosEstadisticas;
}

const LIMIT_LIBROS_POPULARES = 5;
const LIMIT_AUTORES_POPULARES = 10;
const LIMIT_EDITORIALES_POPULARES = 10;
const LIMIT_BUSQUEDAS = 10;

function LibrosEstadisticasLista({ tipoSeccionLibros }: LibrosPopularesProps) {
    const { t } = useTranslation();
    const { telemetryQueries } = useServices();
    const { userData } = useNotificationContext();

    const [rango, setRango] = React.useState<TipoPeriodoTelemetry>(TipoPeriodoTelemetry.DIA);
    const [totalDatos, setTotalDatos] = React.useState<TelemetryRowObject[]>([]);

    const OpcionesBarraMenuMensajes: OpcionTopNavigation[] = [
        { nombre: t("menu:secciones.estadisticas.hoy"), tipoOpcion: TipoPeriodoTelemetry.DIA },
        { nombre: t("menu:secciones.estadisticas.semana"), tipoOpcion: TipoPeriodoTelemetry.SEMANA },
        { nombre: t("menu:secciones.estadisticas.mes"), tipoOpcion: TipoPeriodoTelemetry.MES },
        { nombre: t("menu:secciones.estadisticas.total"), tipoOpcion: TipoPeriodoTelemetry.TOTAL },
    ];

    useEffect(() => {
        if (tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_MAS_FAVORITOS_PROPIOS) {
            setRango(TipoPeriodoTelemetry.SEMANA);
        }
    }, [tipoSeccionLibros]);


    useEffect(() => {
        if (rango === undefined) return;
        switch (tipoSeccionLibros) {
            case TipoSeccionLibrosEstadisticas.LIBROS_POPULARES:
                cargarEstadisticasGenerales();
                break;
            case TipoSeccionLibrosEstadisticas.LIBROS_PROPIOS_POPULARES:
                cargarEstadisticasPropias();
                break;
            case TipoSeccionLibrosEstadisticas.LIBROS_MAS_VISITADOS:
                cargarEstadisticasLibrosMasVisitados();
                break;
            case TipoSeccionLibrosEstadisticas.EDITORIALES_POPULARES:
                cargarEstadisticasEditorialesMasPopulares();
                break;
            case TipoSeccionLibrosEstadisticas.LIBROS_MAS_VENIDOS:
                cargarEstadisticasLibrosMasVendidos();
                break;
            case TipoSeccionLibrosEstadisticas.AUTORES_POPULARES:
                cargarEstadisticasAutoresMasPopulares();
                break;
            case TipoSeccionLibrosEstadisticas.LIBROS_MAS_CONTACTADOS:
                cargarEstadisticasLibrosMasContactados();
                break;
            case TipoSeccionLibrosEstadisticas.LIBROS_MAS_FAVORITOS:
                cargarEstadisticasLibrosMasFavoritos();
                break;
            case TipoSeccionLibrosEstadisticas.LIBROS_MAS_FAVORITOS_PROPIOS:
                cargarEstadisticasLibrosMasFavoritosPropios();
                break;

            case TipoSeccionLibrosEstadisticas.BUSQUEDAS_CON_RESULTADOS:
                cargarEstadisticasBusquedasConResultados();
                break;

            case TipoSeccionLibrosEstadisticas.BUSQUEDAS_SIN_RESULTADOS:
                cargarEstadisticasBusquedasSinResultados();
                break;
        }
    }, [rango]);

    function cargarEstadisticasGenerales() {
        telemetryQueries?.obtenerLibrosPopulares(rango, LIMIT_LIBROS_POPULARES).then((response) => {
            if (response.status == 200) {
                setTotalDatos(response.data);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        });
    }

    function cargarEstadisticasPropias() {
        if (userData == null) return;
        telemetryQueries?.obtenerLibrosPopularesByIdUser(userData?.id_user!, TipoPeriodoTelemetry.TOTAL, LIMIT_LIBROS_POPULARES).then((response) => {
            if (response.status == 200) {
                setTotalDatos(response.data);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        });
    }
    function cargarEstadisticasAutoresMasPopulares() {
        if (userData == null) return;
        telemetryQueries?.obtenerAutoresMasVisitados(userData?.id_user!, TipoPeriodoTelemetry.TOTAL, LIMIT_AUTORES_POPULARES).then((response) => {
            if (response.status == 200) {
                const telemetry_row_objects: TelemetryRowObject[] = response.data.map((editorial) => {
                    return {
                        id_article: 0,
                        articulo_categoria: "",
                        title: editorial.texto,
                        image: "",
                        author: "",
                        visto: editorial.visitas
                    };
                });

                setTotalDatos(telemetry_row_objects);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        });
    }

    function cargarEstadisticasLibrosMasContactados() {
        if (userData == null) return;
        telemetryQueries?.obtenerLibrosMasContactados(userData?.id_user!, TipoPeriodoTelemetry.TOTAL, LIMIT_LIBROS_POPULARES).then((response) => {
            if (response.status == 200) {
                setTotalDatos(response.data);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        });
    }

    function cargarEstadisticasLibrosMasFavoritos() {
        if (userData == null) return;
        telemetryQueries?.obtenerLibrosMasFavoritos(rango, LIMIT_LIBROS_POPULARES).then((response) => {
            if (response.status == 200) {
                setTotalDatos(response.data);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        });
    }

    function cargarEstadisticasLibrosMasFavoritosPropios() {
        if (userData == null) return;
        telemetryQueries?.obtenerLibrosConMasFavoritosPropios(userData?.id_user!, LIMIT_LIBROS_POPULARES).then((response) => {
            if (response.status == 200) {
                setTotalDatos(response.data);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        }
        );
    }

    function cargarEstadisticasLibrosMasVisitados() {
        if (userData == null) return;
        telemetryQueries?.obtenerLibrosMasVisitados(userData?.id_user!, TipoPeriodoTelemetry.TOTAL, LIMIT_LIBROS_POPULARES).then((response) => {
            if (response.status == 200) {
                setTotalDatos(response.data);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        });
    }

    function cargarEstadisticasEditorialesMasPopulares() {
        if (userData == null) return;
        telemetryQueries?.obtenerEditorialesMasVisitadas(userData?.id_user!, TipoPeriodoTelemetry.TOTAL, LIMIT_EDITORIALES_POPULARES).then((response) => {
            if (response.status == 200) {
                const telemetry_row_objects: TelemetryRowObject[] = response.data.map((editorial) => {
                    return {
                        id_article: 0,
                        articulo_categoria: "",
                        title: editorial.texto,
                        image: "",
                        author: "",
                        visto: editorial.visitas
                    };
                });
                setTotalDatos(telemetry_row_objects);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        });
    }

    function cargarEstadisticasLibrosMasVendidos() {
        if (userData == null) return;
        telemetryQueries?.obtenerLibrosMasVendidos(userData?.id_user!, TipoPeriodoTelemetry.TOTAL, LIMIT_LIBROS_POPULARES).then((response) => {
            if (response.status == 200) {
                setTotalDatos(response.data);
            }
        }).catch((error) => {
            console.log('Error al cargar total libros', error);
        });
    }

    function cargarEstadisticasBusquedasConResultados() {
        telemetryQueries?.obtenerBusquedasResultados(TipoBusquedaTelemetry.CON_RESULTADOS, rango, LIMIT_BUSQUEDAS).then((response) => {
            if (response.status == 200) {
                //Transforma response.data a Article[]
                const busquedas: TelemetryRowObject[] = response.data.map((busqueda: BusquedaResultadoTelemetry) => {
                    return {
                        id_article: 0,
                        articulo_categoria: busqueda.texto_busqueda,
                        title: busqueda.texto_busqueda,
                        image: "",
                        author: "",
                        num_favoritos: 0,
                        visto: busqueda.frecuencia
                    };
                });
                setTotalDatos(busquedas);
            }
        }
        ).catch((error) => {
            console.log('Error al cargar busquedas con resultados', error);
        }
        );
    }

    function cargarEstadisticasBusquedasSinResultados() {
        telemetryQueries?.obtenerBusquedasResultados(TipoBusquedaTelemetry.SIN_RESULTADOS, rango, LIMIT_BUSQUEDAS).then((response) => {
            if (response.status == 200) {
                //Transforma response.data a Article[]
                const busquedas: TelemetryRowObject[] = response.data.map((busqueda: BusquedaResultadoTelemetry) => {
                    return {
                        id_article: 0,
                        articulo_categoria: busqueda.texto_busqueda,
                        title: busqueda.texto_busqueda,
                        image: "",
                        author: "",
                        num_favoritos: 0,
                        visto: busqueda.frecuencia
                    };
                });
                setTotalDatos(busquedas);
            }
        }).catch((error) => {
            console.log('Error al cargar busquedas sin resultados', error);
        });
    }


    return (
        <section style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {/* <TituloPrincipalH1 texto={t("menu:secciones.mensajes.titulo")} /> */}
            <section style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', width: "100%" }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', width: '100%' }}>
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_POPULARES && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.libros_populares")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_PROPIOS_POPULARES && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.mis_libros")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_MAS_VISITADOS && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.libros_mas_visitados")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.EDITORIALES_POPULARES && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.editoriales_mas_populares")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_MAS_VENIDOS && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.libros_mas_vendidos")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.AUTORES_POPULARES && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.autores_mas_populares")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_MAS_CONTACTADOS && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.libros_mas_contactados")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_MAS_FAVORITOS && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.libros_mas_favoritos")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_MAS_FAVORITOS_PROPIOS && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.libros_mas_favoritos")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.BUSQUEDAS_CON_RESULTADOS && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.busquedas_con_resultados")} />
                    )}
                    {tipoSeccionLibros === TipoSeccionLibrosEstadisticas.BUSQUEDAS_SIN_RESULTADOS && (
                        <TituloSeccionH2 texto={t("menu:secciones.estadisticas.libros.busquedas_sin_resultados")} />
                    )}
                </div>
                {(tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_POPULARES ||
                    tipoSeccionLibros === TipoSeccionLibrosEstadisticas.LIBROS_MAS_FAVORITOS ||
                    tipoSeccionLibros === TipoSeccionLibrosEstadisticas.BUSQUEDAS_CON_RESULTADOS ||
                    tipoSeccionLibros === TipoSeccionLibrosEstadisticas.BUSQUEDAS_SIN_RESULTADOS) && (
                        <TopNavigation
                            opciones={OpcionesBarraMenuMensajes}
                            opcionActual={rango.toString()}
                            setOpcionActual={(opcion) => setRango(opcion as TipoPeriodoTelemetry)}
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 5,
                                flexShrink: 0,
                            }}
                        />
                    )
                }

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        width: '100%',
                        maxWidth: '100%',
                        gap: '1em',
                        padding: '1em',
                        boxSizing: 'border-box'
                    }}
                >
                    {totalDatos.map((datoTelemetria: TelemetryRowObject) => (
                        <CardDatoHorizontal
                            key={`${datoTelemetria.articulo_categoria}-${datoTelemetria.id_article}-${datoTelemetria.isbn ?? 'sin-isbn'}-${datoTelemetria.title}`}
                            titulo={datoTelemetria.title}
                            texto={datoTelemetria.author}
                            imagenLibro={obtenerImagenes(datoTelemetria.image, "").image_preview}
                            isbn={datoTelemetria.isbn}
                            favoritos={datoTelemetria.num_favoritos != 0 ? datoTelemetria.num_favoritos : undefined}
                            visitas={datoTelemetria.visto}
                        />
                    ))}

                    {totalDatos.length === 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '2em' }}>
                            <RowNoData texto={t("menu:secciones.estadisticas.libros.no_hay_datos")} />
                        </div>
                    )}

                </div>
            </section>
        </section>
    );
}

export default LibrosEstadisticasLista;